/**
 * A link that saves a Frappe file, and an image that shows one — the ONLY way either should be
 * done: a plain `<a href>` / `<img src>` sends no Clerk bearer and the deployed proxy answers 401
 * (`lib/api/fileDownload.ts` has the story). Both go through the cached attachment loader, so a
 * file the preview already fetched costs no second download.
 */
import { useState, type ReactNode } from 'react'
import { useCachedAttachmentLoader } from '../lib/api/attachmentBlob'
import { downloadFile, useFileObjectUrl } from '../lib/api/fileDownload'

export function FileLink({ href, name, className = 'linkish', children, onError }: {
  /** The `fileHref` URL; `null` renders nothing, as the old `href` guard did. */
  href: string | null | undefined
  /** The filename the save dialog offers. */
  name: string
  className?: string
  children?: ReactNode
  onError?: (message: string) => void
}) {
  const loadBlob = useCachedAttachmentLoader()
  const [busy, setBusy] = useState(false)
  const [failed, setFailed] = useState<string | null>(null)
  if (!href) return null
  return (
    <a
      className={className}
      href={href}
      download={name}
      aria-busy={busy}
      title={failed ?? undefined}
      onClick={(e) => {
        e.preventDefault()
        if (busy) return
        setBusy(true)
        setFailed(null)
        downloadFile(href, name, loadBlob)
          .catch((error: unknown) => {
            const message = error instanceof Error ? error.message : String(error)
            setFailed(message)
            onError?.(message)
          })
          .finally(() => setBusy(false))
      }}
    >
      {children ?? name}
      {busy ? ' …' : ''}
      {failed ? ` — ${failed}` : ''}
    </a>
  )
}

export function FileImage({ src, alt, className, style }: { src: string | null | undefined; alt: string; className?: string; style?: React.CSSProperties }) {
  const file = useFileObjectUrl(src ?? null)
  if (file.error) return <span className="muted">{alt} — {file.error}</span>
  if (!file.src) return <span className="muted">Loading {alt}…</span>
  return <img src={file.src} alt={alt} className={className} style={style} />
}
